import type { PresenceEvent, PresenceRecord } from '@/types';
import { logger } from '@/core/logger';
import { addPresenceRecord } from '@/core/offline-store';
import { onPresenceChange } from './sensor-bridge';

let counter = 0;
let sessionStart: string | null = null;
let sessionAudienceMax = 0;
let unsubscribe: (() => void) | null = null;

function generateId(): string {
  return `pres-${Date.now()}-${++counter}`;
}

export function startTracking(boardId: string): void {
  if (unsubscribe) return;

  unsubscribe = onPresenceChange((event: PresenceEvent) => {
    if (event.present) {
      // Start or continue session
      if (!sessionStart) {
        sessionStart = event.timestamp;
        sessionAudienceMax = event.audienceCount;
      } else {
        sessionAudienceMax = Math.max(sessionAudienceMax, event.audienceCount);
      }
    } else {
      // End session — record it
      if (sessionStart) {
        const durationSec = Math.round(
          (new Date(event.timestamp).getTime() - new Date(sessionStart).getTime()) / 1000,
        );

        if (durationSec >= 1) {
          const record: PresenceRecord = {
            id: generateId(),
            boardId,
            timestamp: sessionStart,
            durationSec,
            audienceCount: sessionAudienceMax,
            synced: false,
          };
          addPresenceRecord(record);
          logger.debug('[PresenceTracker] Session ended', {
            durationSec,
            audience: sessionAudienceMax,
          });
        }

        sessionStart = null;
        sessionAudienceMax = 0;
      }
    }
  });

  logger.info('[PresenceTracker] Started tracking', { boardId });
}

export function stopTracking(): void {
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
  sessionStart = null;
  sessionAudienceMax = 0;
}
